import moment from 'moment/moment';
import qs from 'qs';
import _ from 'lodash';
import { formatPrice } from './formatData';
import { basicRankingNumber, phoneUSALength } from '../constants/general';
import regex from '../constants/regExps';
import { excludeFromSearchCriteria } from '../constants/ui';
import { monthDayYearWithSlashFormat } from '../constants/date';

export const getDollarPrice = value => value / 100;
export const getCentPrice = value => parseInt(value * 100, 10);

export const getCostPayRate = (fieldName, costPayRateList, skillLevelId = basicRankingNumber) => {
  if (costPayRateList.length === 0) {
    return 0;
  }
  const costPayRateForSelectedLevel = costPayRateList.find(listItem => skillLevelId === listItem.skill_level_id);
  if (costPayRateForSelectedLevel) {
    return costPayRateForSelectedLevel[fieldName];
  }
};

export const getJobCostRateDollarPrice = (costPayRateList, skillLevelId) => {
  return getDollarPrice(getCostPayRate('cost_rate', costPayRateList, skillLevelId));
};

export const getWorkerPayRateDollarPrice = (costPayRateList, skillLevelId) => {
  return getDollarPrice(getCostPayRate('pay_rate', costPayRateList, skillLevelId));
};

export const getEstCost = (jobCostRate, hours, quantityOfWorkers) => {
  return formatPrice(getEstCostValue(jobCostRate, hours, quantityOfWorkers));
};

export const getEstCostValue = (jobCostRate = 0, hours = 0, quantityOfWorkers = 0) => {
  return jobCostRate * hours * quantityOfWorkers;
};

export const sortArrayByFieldAsc = (arrayList, fieldKey) => {
  function compareByName (itemA, itemB) {
    const itemLabelA = typeof itemA[fieldKey] === 'string' ? itemA[fieldKey].toLowerCase() : itemA[fieldKey];
    const itemLabelB = typeof itemB[fieldKey] === 'string' ? itemB[fieldKey].toLowerCase() : itemB[fieldKey];
    if (itemLabelA < itemLabelB) {
      return -1;
    } else {
      return 1;
    }
  }
  return arrayList.sort(compareByName);
};

export const sortArrayByDate = (arrayList, fieldKey, direction) => {
  function compareByDate (itemA, itemB) {
    if (moment(itemA[fieldKey]).isBefore(itemB[fieldKey])) {
      return direction === 'desc' ? 1 : -1;
    } else {
      return direction === 'desc' ? -1 : 1;
    }
  }
  return arrayList.sort(compareByDate);
};

/**
 *
 * @param {string} inputValue
 * @returns {string}
 *
 * */
const replaceChars = inputValue => {
  if (inputValue.length >= phoneUSALength) {
    return inputValue.replace(/(\d{3})(\d{3})(\d{4})/g, '$1-$2-$3 ');
  }
  return inputValue.replace(/(\d{3})/g, '$1-');
};

/**
 *
 * @param {string} phoneNumber
 * @returns {string}
 *
 * */
export const visuallyFormatPhoneNumber = phoneNumber => {
  const phoneToReplace = phoneNumber.split('').filter(char => !/\D/.test(char)).join('');
  return replaceChars(phoneToReplace).trim();
};

const maxWidthForReactTables = 250;
const spacingForReactTables = 11;

export const getColumnWidth = (rows, accessor, headerText) => {
  const cellLength = Math.max(
    ...rows.map(row => (`${row[accessor]}` || '').length),
    headerText.length
  );
  return Math.min(maxWidthForReactTables, cellLength * spacingForReactTables);
};

export const getPagesCount = (itemsQuantity, pageSize) => Math.ceil(itemsQuantity / pageSize);

export const isIntegerOrDecimalWithTwoDigits = value => /^\d+.?(\d{1,2})?$/.test(value);

export const tryParseMonetaryValue = value => {
  if (value === '') {
    return value;
  }
  if (isIntegerOrDecimalWithTwoDigits(value)) {
    return parseFloat(value, 10);
  }
  return null;
};

/**
 *
 * @param {array} skillLevels
 * @return {number}
 *
 * */
export function getBasicSkillLevelId (skillLevels) {
  if (!skillLevels.length) {
    return null;
  }

  const basicSkillLevel = skillLevels.find(skillLevel => skillLevel.ranking_number === basicRankingNumber);

  if (!basicSkillLevel) {
    return null;
  }

  return basicSkillLevel.skill_level_id;
}

/**
 *
 * @param {array} allSkills
 * @param {array} usedSkills
 * @return {array}
 *
 * */
export function getAvailableSkills (allSkills, usedSkills) {
  return allSkills.filter(function (skill) {
    return !usedSkills.some(function (usedSkill) {
      return usedSkill.job_skill_id === skill.id;
    });
  });
}

/**
 * @param {array} data
 * @param {object} item
 * */
export function extractDataFromSearch (data, item) {
  if (item.id) {
    return [
      ...data.filter(dataItem => dataItem.id !== item.id),
      item
    ];
  }

  return data;
}

/**
 *
 * @param {object} formData
 * @returns {object}
 *
 */
export const setStripeCardData = formData => {
  const { name, month, year, cvc, cardNumber, address, city, state, zip } = formData;
  const stripeData = {
    'card[number]': cardNumber.replace(regex.creditCardStripSymbols, ''),
    'card[cvc]': cvc,
    'card[exp_month]': month,
    'card[exp_year]': year,
    'card[name]': name,
    'card[address_line1]': address,
    'card[address_city]': city,
    'card[address_state]': state,
    'card[address_zip]': zip
  };
  return qs.stringify(stripeData);
};

/**
 *
 * @description Returns true if a remainder from a division === 0
 *
 * @param {number} dividend
 * @param {number} divider
 * @return {boolean}
 */
export function isNumberMultipleBy (dividend, divider) {
  return !(dividend % divider);
}

/**
 * @param {object} filterParams
 * @param {object} initialFilter
 * @return {array}
 */
export function getSelectedFilterValuesForUI (filterParams, initialFilter = {}) {
  const filter = [];

  for (let key in filterParams) {
    if (excludeFromSearchCriteria.includes(key)) {
      continue;
    }

    const filterParam = filterParams[key];

    if (!_.isUndefined(filterParam) && filterParam !== initialFilter[key]) {
      let filterValue = filterParam;

      if (_.isArray(filterParam)) {
        if (!filterParam.length) {
          continue;
        }
      }

      if (filterValue instanceof moment) {
        filterValue = filterValue.format(monthDayYearWithSlashFormat);
      }

      filter.push({ key, value: filterValue });
    }
  }

  return filter;
}

/**
 * @param {array} skills
 * @return {array}
 */
export function mapSkillsListToUI (skills = []) {
  return skills.map(skill => ({ label: skill.skill_label, value: skill.id }));
}

/**
 * @param {string} link
 * @return {string}
 */
export function renderLinkPath (link) {
  return link.indexOf('http') === 0 ? link : `//${link}`;
}

/**
 * @param {object} data
 * @return {string}
 */
export function serializeToQueryString (data) {
  return Object.entries(data)
    .map(([key,value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}



// WEBPACK FOOTER //
// ./src/helpers/general.js